import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { costuraApi } from '../services/api';
import { PageHeader, Loading, EmptyState, BadgeLote } from '../components/ui';
import { formatarMoeda, formatarData, formatarTelefone, iniciais } from '../lib/format';
import {
  Plus, Printer, CheckCircle2, Banknote, Scissors, PackageOpen, History, Phone, Trash2, X,
} from 'lucide-react';

type ItemLote = {
  produto: string;
  tecido: string;
  cor: string;
  tamanho: string;
  quantidade: string;
  valorUnitario: string;
};

const itemVazio = (): ItemLote => ({
  produto: '', tecido: '', cor: '', tamanho: '', quantidade: '1', valorUnitario: '',
});

const abas = [
  { key: 'lotes', label: 'Lotes', icon: PackageOpen },
  { key: 'costureiras', label: 'Costureiras', icon: Scissors },
  { key: 'pagamentos', label: 'Pagamentos', icon: History },
] as const;

export function CosturaPage() {
  const [aba, setAba] = useState<'lotes' | 'costureiras' | 'pagamentos'>('lotes');
  const [mostrarNovoLote, setMostrarNovoLote] = useState(false);
  const [costureiraId, setCostureiraId] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState<ItemLote[]>([itemVazio()]);
  const [erroForm, setErroForm] = useState('');
  const [mostrarNovaCostureira, setMostrarNovaCostureira] = useState(false);
  const [nomeCostureira, setNomeCostureira] = useState('');
  const [telefoneCostureira, setTelefoneCostureira] = useState('');
  const [erroCostureira, setErroCostureira] = useState('');

  const queryClient = useQueryClient();

  const { data: lotes, isLoading: carregandoLotes } = useQuery({
    queryKey: ['lotes'],
    queryFn: () => costuraApi.listarLotes().then(r => r.data),
  });

  const { data: costureiras, isLoading: carregandoCostureiras } = useQuery({
    queryKey: ['costureiras'],
    queryFn: () => costuraApi.listarCostureiras().then(r => r.data),
  });

  const proximoNumero = (lotes?.data || []).reduce(
    (max: number, l: any) => Math.max(max, l.numero || 0), 0,
  ) + 1;

  const criarLote = useMutation({
    mutationFn: (data: any) => costuraApi.criarLote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lotes'] });
      fecharModal();
    },
    onError: (e: any) => {
      setErroForm(e?.response?.data?.error || 'Erro ao salvar o lote. Verifique os dados.');
    },
  });

  const criarCostureira = useMutation({
    mutationFn: (data: any) => costuraApi.criarCostureira(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['costureiras'] });
      setMostrarNovaCostureira(false);
      setNomeCostureira('');
      setTelefoneCostureira('');
      setErroCostureira('');
    },
    onError: () => setErroCostureira('Erro ao cadastrar. Tente novamente.'),
  });

  function salvarCostureira() {
    setErroCostureira('');
    if (!nomeCostureira.trim()) {
      setErroCostureira('Informe o nome da costureira.');
      return;
    }
    criarCostureira.mutate({
      nome: nomeCostureira.trim(),
      telefone: telefoneCostureira.trim() || undefined,
    });
  }

  function fecharModal() {
    setMostrarNovoLote(false);
    setCostureiraId('');
    setObservacoes('');
    setItens([itemVazio()]);
    setErroForm('');
  }

  function atualizarItem(idx: number, campo: keyof ItemLote, valor: string) {
    setItens(prev => prev.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)));
  }

  function removerItem(idx: number) {
    setItens(prev => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  }

  const totais = itens.reduce(
    (acc, it) => {
      const qtd = parseInt(it.quantidade) || 0;
      const valor = parseFloat(it.valorUnitario.replace(',', '.')) || 0;
      acc.quantidade += qtd;
      acc.valor += qtd * valor;
      return acc;
    },
    { quantidade: 0, valor: 0 },
  );

  function salvarLote() {
    setErroForm('');
    if (!costureiraId) {
      setErroForm('Selecione uma costureira.');
      return;
    }
    const itensValidos = itens
      .filter(it => it.produto.trim())
      .map(it => ({
        produto: it.produto.trim(),
        tecido: it.tecido.trim() || '-',
        cor: it.cor.trim() || '-',
        tamanho: it.tamanho.trim() || '-',
        quantidade: parseInt(it.quantidade) || 0,
        valorUnitario: parseFloat(it.valorUnitario.replace(',', '.')) || 0,
      }));

    if (itensValidos.length === 0) {
      setErroForm('Adicione pelo menos uma peça com descrição.');
      return;
    }
    if (itensValidos.some(it => it.quantidade <= 0 || it.valorUnitario <= 0)) {
      setErroForm('Quantidade e custo unitário devem ser maiores que zero.');
      return;
    }

    criarLote.mutate({
      costureiraId,
      observacoes: observacoes.trim() || undefined,
      itens: itensValidos,
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Costura"
        descricao="Lotes enviados, costureiras parceiras e pagamentos."
      >
        <button onClick={() => { setAba('lotes'); setMostrarNovoLote(true); }} className="btn-primary">
          <Plus className="w-4 h-4" />
          Novo Lote
        </button>
      </PageHeader>

      {/* Abas */}
      <div className="inline-flex gap-1 bg-white border border-ink-200/70 shadow-card p-1 rounded-xl">
        {abas.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setAba(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
              aba === key
                ? 'bg-ink-950 text-white shadow-sm'
                : 'text-ink-500 hover:text-ink-800 hover:bg-ink-50'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {aba === 'lotes' && !mostrarNovoLote && (
        <div className="table-shell">
          {carregandoLotes ? (
            <Loading />
          ) : (lotes?.data?.length || 0) === 0 ? (
            <EmptyState
              icon={PackageOpen}
              titulo="Nenhum lote cadastrado"
              descricao="Crie um lote para enviar peças às costureiras."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Lote</th>
                    <th>Costureira</th>
                    <th>Data de Envio</th>
                    <th>Itens</th>
                    <th>Valor Total</th>
                    <th>Situação</th>
                    <th className="!text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {lotes?.data?.map((lote: any) => (
                    <tr key={lote.id}>
                      <td className="font-display font-semibold text-ink-900">#{lote.numero}</td>
                      <td className="font-medium text-ink-800">{lote.costureira?.nome}</td>
                      <td className="text-ink-500">{formatarData(lote.dataEnvio)}</td>
                      <td className="text-ink-500">{lote.itens?.length || 0} itens</td>
                      <td className="font-semibold text-ink-900">{formatarMoeda(lote.valorTotal)}</td>
                      <td><BadgeLote situacao={lote.situacao} /></td>
                      <td>
                        <div className="flex items-center justify-center gap-1">
                          <button className="icon-btn hover:!bg-emerald-50 hover:!text-emerald-600" title="Receber lote">
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button className="icon-btn" title="Imprimir">
                            <Printer className="w-4 h-4" />
                          </button>
                          <button className="icon-btn hover:!bg-brand-50 hover:!text-brand-600" title="Registrar pagamento">
                            <Banknote className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {aba === 'costureiras' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setMostrarNovaCostureira(v => !v)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4" />
              Nova Costureira
            </button>
          </div>

          {mostrarNovaCostureira && (
            <div className="card p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className="input-label">Nome *</label>
                  <input
                    value={nomeCostureira}
                    onChange={e => setNomeCostureira(e.target.value)}
                    placeholder="Ex.: Regina"
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="input-label">Telefone</label>
                  <input
                    value={telefoneCostureira}
                    onChange={e => setTelefoneCostureira(e.target.value)}
                    placeholder="(00) 90000-0000"
                    className="input-field"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={salvarCostureira}
                    disabled={criarCostureira.isPending}
                    className="btn-primary"
                  >
                    {criarCostureira.isPending ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    onClick={() => setMostrarNovaCostureira(false)}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
              {erroCostureira && (
                <p className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {erroCostureira}
                </p>
              )}
            </div>
          )}

          {carregandoCostureiras ? (
            <div className="card"><Loading /></div>
          ) : (costureiras?.length || 0) === 0 ? (
            <div className="table-shell">
              <EmptyState
                icon={Scissors}
                titulo="Nenhuma costureira cadastrada"
                descricao="Cadastre as costureiras parceiras para enviar lotes."
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {costureiras?.map((c: any) => (
                <div key={c.id} className="card p-5 flex items-center gap-4 transition-all duration-200 hover:shadow-pop hover:-translate-y-0.5">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-100 to-violet-200 text-violet-700 flex items-center justify-center text-sm font-bold shrink-0">
                    {iniciais(c.nome)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink-900 truncate">{c.nome}</p>
                    {c.telefone && (
                      <p className="flex items-center gap-1.5 text-[13px] text-ink-500 mt-0.5">
                        <Phone className="w-3.5 h-3.5" />
                        {formatarTelefone(c.telefone)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {aba === 'pagamentos' && (
        <div className="table-shell">
          <EmptyState
            icon={History}
            titulo="Histórico de pagamentos"
            descricao="Os pagamentos registrados às costureiras aparecerão aqui."
          />
        </div>
      )}

      {/* ── Formulário inline: Novo Lote de Costura ─────── */}
      {mostrarNovoLote && (
        <div className="card p-6 space-y-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display text-lg font-bold text-ink-950 tracking-tight">
                Novo Lote de Costura — L{String(proximoNumero).padStart(4, '0')}
              </h2>
              <p className="text-[13px] text-ink-500 mt-0.5">
                Os dados salvos alimentam a impressão e o histórico.
              </p>
            </div>
            <button onClick={fecharModal} className="icon-btn -mr-2 -mt-1" aria-label="Fechar">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="input-label">Costureira *</label>
              <select
                value={costureiraId}
                onChange={e => setCostureiraId(e.target.value)}
                className="input-field"
              >
                <option value="">Selecione...</option>
                {(costureiras || []).map((c: any) => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
              {(costureiras?.length || 0) === 0 && !carregandoCostureiras && (
                <p className="text-[12px] text-amber-600 mt-1">
                  Nenhuma costureira cadastrada — cadastre na aba "Costureiras".
                </p>
              )}
            </div>
            <div>
              <label className="input-label">Observações gerais</label>
              <input
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
                placeholder="Opcional. Urgências, combinações ou recados."
                className="input-field"
              />
            </div>
          </div>

          {/* Peças do lote */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">Peças do lote</h3>
                <p className="text-[12px] text-ink-500">Descreva cada peça enviada para costura.</p>
              </div>
              <button
                type="button"
                onClick={() => setItens(prev => [...prev, itemVazio()])}
                className="btn-secondary text-[13px]"
              >
                <Plus className="w-4 h-4" /> Adicionar peça
              </button>
            </div>

            <div className="space-y-2">
              <div className="hidden md:grid md:grid-cols-[2fr_1fr_1fr_0.8fr_0.7fr_1fr_0.9fr_32px] gap-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400 px-1">
                <span>Descrição / Modelo</span>
                <span>Tecido</span>
                <span>Cor</span>
                <span>Tam.</span>
                <span>Qtd.</span>
                <span>Custo unit.</span>
                <span className="text-right">Total</span>
                <span />
              </div>
              {itens.map((it, idx) => {
                const totalLinha =
                  (parseInt(it.quantidade) || 0) *
                  (parseFloat(it.valorUnitario.replace(',', '.')) || 0);
                return (
                  <div key={idx} className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr_0.8fr_0.7fr_1fr_0.9fr_32px] gap-2 items-center">
                    <input
                      value={it.produto}
                      onChange={e => atualizarItem(idx, 'produto', e.target.value)}
                      placeholder="Ex.: Camiseta Manga Longa"
                      className="input-field col-span-2 md:col-span-1"
                    />
                    <input
                      value={it.tecido}
                      onChange={e => atualizarItem(idx, 'tecido', e.target.value)}
                      placeholder="Ex.: Dry Fit"
                      className="input-field"
                    />
                    <input
                      value={it.cor}
                      onChange={e => atualizarItem(idx, 'cor', e.target.value)}
                      placeholder="Ex.: Preto"
                      className="input-field"
                    />
                    <input
                      value={it.tamanho}
                      onChange={e => atualizarItem(idx, 'tamanho', e.target.value)}
                      placeholder="Tam."
                      className="input-field"
                    />
                    <input
                      type="number"
                      min={1}
                      value={it.quantidade}
                      onChange={e => atualizarItem(idx, 'quantidade', e.target.value)}
                      className="input-field"
                    />
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={it.valorUnitario}
                      onChange={e => atualizarItem(idx, 'valorUnitario', e.target.value)}
                      placeholder="R$ 0,00"
                      className="input-field"
                    />
                    <span className="text-right text-sm font-semibold text-ink-900">
                      {formatarMoeda(totalLinha)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removerItem(idx)}
                      className="icon-btn hover:!bg-red-50 hover:!text-red-600"
                      title="Remover peça"
                      disabled={itens.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totais */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-ink-50 border border-ink-100 px-4 py-3">
              <p className="text-[12px] text-ink-500">Quantidade total</p>
              <p className="text-lg font-bold text-ink-900">{totais.quantidade}</p>
            </div>
            <div className="rounded-xl bg-ink-50 border border-ink-100 px-4 py-3">
              <p className="text-[12px] text-ink-500">Total do lote</p>
              <p className="text-lg font-bold text-ink-900">{formatarMoeda(totais.valor)}</p>
            </div>
          </div>

          {erroForm && (
            <p className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {erroForm}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={salvarLote}
              disabled={criarLote.isPending}
              className="btn-primary"
            >
              {criarLote.isPending ? 'Salvando...' : 'Salvar Lote'}
            </button>
            <button type="button" onClick={fecharModal} className="btn-secondary">
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
